<template>
  <EditorModal
    :toolbar-right-buttons="toolbarHeaderRightButtons"
    :content-header-toolbar-start="secondToolbarLeftButtons"
    :title="pageSettingStateName"
  >
    <template
      v-if="leftToolbarState.show"
      #sidebar
    >
      <StatsBuilderElementsPanel v-if="leftToolbarState.panel === StatsEditorPanelEnum.ELEMENTS_LIST_PANEL" />
      <Panel
        v-if="leftToolbarState.panel === StatsEditorPanelEnum.WIDGET_EDIT_PANEL"
        class="panel-sidebar-builder-stats"
      >
        <template #header>
          <div class="panel-header-content">
            <i
              size="xs"
              class="fas fa-cogs mr-2"
            />
            <h4>{{ t('statsEditor.widgets.configPanel.header') }}</h4>
          </div>
        </template>

        <template #icons>
          <i
            v-tooltip="t('statsEditor.sidebarPanel.close')"
            class="far fa-times icons-header-panel"
            @click="closeWidgetEditorPanel"
          />
        </template>
        <div
          v-if="selectedWidgetState !== null"
          class="configuration-wrapper"
        >
          <component
            :is="`${selectedWidgetState.component}Config`"
            :key="`${selectedWidgetState.location.rowId}-${selectedWidgetState.location.columnId}-${selectedWidgetState.location.widgetIndex}-${selectedWidgetState.location.panelData}`"
            v-model="selectedWidgetState.data"
            v-model:form-validation="formValidation"
            :predefined-metrics="groupedCategoriesPredefinedMetrics"
          />
          <div
            class="configuration-action-container flex justify-content-center"
          >
            <Button
              :label="t('statsEditor.widgets.configPanel.saveButton')"
              @click="saveWidget"
            />
          </div>
        </div>
      </Panel>
      <Panel
        v-else-if="leftToolbarState.panel === StatsEditorPanelEnum.SETTINGS_PANEL"
        class="panel-sidebar-builder-stats"
      >
        <template #header>
          <div class="panel-header-content">
            <i
              size="xs"
              class="fas fa-cogs mr-2"
            />
            <h4>{{ t('statsEditor.sidebarPanel.settings.header') }}</h4>
          </div>
        </template>

        <template #icons>
          <i
            v-tooltip="t('statsEditor.sidebarPanel.close')"
            class="far fa-times icons-header-panel"
            @click="closeWidgetEditorPanel"
          />
        </template>
        <div>
          <StatsSettingsPanel
            v-if="pageTypeState === StatsType.STATS"
            v-model:setting-panel-validation-errors="formValidation"
          />
          <EmailSettingsPanel
            v-else-if="pageTypeState === StatsType.EMAIL"
            v-model:setting-panel-validation-errors="formValidation"
          />
        </div>
      </Panel>
      <Panel
        v-else-if="leftToolbarState.panel === StatsEditorPanelEnum.FILTERS_PANEL"
        class="panel-sidebar-builder-stats"
      >
        <template #header>
          <div class="panel-header-content">
            <i
              size="xs"
              class="fas fa-cogs mr-2"
            />
            <h4>{{ t('statsEditor.sidebarPanel.filtersTitle') }}</h4>
          </div>
        </template>

        <template #icons>
          <i
            v-tooltip="t('statsEditor.sidebarPanel.close')"
            class="far fa-times icons-header-panel"
            @click="closeWidgetEditorPanel"
          />
        </template>
        <div>
          <StatsBuilderFilters :hide-visible-icon="pageTypeState === StatsType.EMAIL" />
        </div>
      </Panel>
    </template>
    <template #content>
      <div class="surface-ground p-2">
        <StatsBuilder
          :page-rows-state="pageRowsState"
        />
      </div>
    </template>
  </EditorModal>
  <ConfirmDialog group="statsEditor" />
  <SendTestModal
    @on-validate="(emitParameters) => sendTestReport(emitParameters)"
  />
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  Ref,
  nextTick,
} from 'vue';
import EditorModal from '@/components/modals/EditorModal.vue';
import {
  PrimvueMenuModel, StatsEditorPanelEnum, StatsType,
} from '@/types';
import { useI18n } from 'vue-i18n';
import StatsBuilder from '@/components/stats-builder/StatsBuilder.vue';
// eslint-disable-next-line import/no-cycle
import { useStore } from '@/store';
import { useConfirm } from 'primevue/useconfirm';
import ComponentsStatsBuilderWidgetConfig from '@/components/stats-builder/widgets';
import {
  GlobalEmailSettingsData,
  StatsBuilderRowConfig,
  StatsBuilderSelectedWidget,
} from '@/types/stats-editor-types';
import Panel from 'primevue/panel';
import Button from 'primevue/button';
import { showToastError, showToastSuccess } from '@/helpers';
import Tooltip from 'primevue/tooltip';
import StatsSettingsPanel from '@/components/stats-builder/settingsPanel/StatsSettingsPanel.vue';
import EmailSettingsPanel from '@/components/stats-builder/settingsPanel/EmailSettingsPanel.vue';
import { pageSettingsValidate, UpdateUsersPagesConfiguration } from '@/composables/Statistics/usersPagesConfiguration';
import { UsersPagesConfigurationUpdateInputItem } from '@/types/generated-types/graphql';
import StatsBuilderFilters from '@/components/stats-builder/StatsBuilderFilters.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import StatsBuilderElementsPanel from '@/components/stats-builder/StatsBuilderElementsPanel.vue';
import { localizedTextInputValidator } from '@/helpers/CustomValidator';
import SendTestModal, { Suggestion } from '@/components/modals/SendTestModal.vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import { ChartType, StatsWidgetType } from '@/types/generated-types/graphql';
import { getChannelTokens, getChannelDisplayName } from '@/constants/statsChannels';
import type {
  StatsPredefinedMetricFilterOption,
  StatsPredefinedMetricFilters,
  StatsPredefinedMetricGroup,
  StatsPredefinedMetricOption,
  StatsPredefinedMetricValue,
} from '@/types/stats-predefined-metrics';

export default defineComponent({
  name: 'StatsEditor',

  components: {
    SendTestModal,
    StatsBuilderElementsPanel,
    ConfirmDialog,
    StatsBuilderFilters,
    EmailSettingsPanel,
    StatsSettingsPanel,
    StatsBuilder,
    EditorModal,
    Panel,
    Button,
    ...ComponentsStatsBuilderWidgetConfig,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();
    const confirm = useConfirm();

    type PublicMetricFilterMetadata = {
      placeholder?: string | string[];
      availableValues?: unknown[];
    };

    type PublicMetricFiltersMetadata = Record<string, PublicMetricFilterMetadata>;

    interface PublicMetricWidgetBase {
      id: string;
      type: 'chart' | 'kpi' | 'table';
      name: string;
      description?: string | null;
      availableChartTypes: string[];
      availableFilters: string[];
      filtersMetadata?: PublicMetricFiltersMetadata;
      requiresDateRange: boolean;
      supportsTotals?: boolean;
    }

    interface PublicMetricChartVariant {
      id: string;
      key: string;
      name: string;
      description?: string | null;
      availableChartTypes: string[];
      availableFilters: string[];
      filtersMetadata?: PublicMetricFiltersMetadata;
      requiresDateRange: boolean;
    }

    interface PublicMetricChartGroup {
      key: string;
      name: string;
      description?: string | null;
      availableChartTypes: string[];
      availableFilters: string[];
      filtersMetadata?: PublicMetricFiltersMetadata;
      requiresDateRange: boolean;
      variants: PublicMetricChartVariant[];
    }

    interface PublicMetricTableWidget extends PublicMetricWidgetBase {
      type: 'table';
      availableColumns?: Array<{
        field: string;
        header: string;
        type?: string;
        sortable?: boolean;
        filterable?: boolean;
        hidden?: boolean;
      }>;
      defaultColumns?: string[];
    }

    interface PublicMetricChartWidget extends PublicMetricWidgetBase {
      type: 'chart';
      allowMultiple: boolean;
      variants?: PublicMetricChartVariant[];
      groups?: PublicMetricChartGroup[];
    }

    type PublicMetricAvailableWidgets = {
      kpi?: PublicMetricWidgetBase;
      table?: PublicMetricTableWidget;
      chart?: PublicMetricChartWidget;
    };

    interface PublicMetricDefinition {
      id: string;
      name: string;
      description?: string | null;
      category: string;
      availableWidgets: PublicMetricAvailableWidgets;
      availableFilters: string[];
      filtersMetadata?: PublicMetricFiltersMetadata;
      requiresDateRange: boolean;
      isLegacy?: boolean;
    }

    interface PublicMetricGroup {
      id: string;
      name: string;
      description?: string | null;
      metrics: PublicMetricDefinition[];
    }

    // Généralisation : support dynamique de toutes les clés de filtres
    const normalizeFilterKey = (key: string): string => {
      // Conversion des clés legacy pour compatibilité
      const legacyMap: Record<string, string> = {
        bounceType: 'bounce_type',
      };
      return legacyMap[key] || key;
    };

    const chartTypeMap: Record<string, ChartType> = {
      [ChartType.TABLE.toUpperCase()]: ChartType.TABLE,
      [ChartType.BAR.toUpperCase()]: ChartType.BAR,
      [ChartType.DOUGHNUT.toUpperCase()]: ChartType.DOUGHNUT,
      [ChartType.LINE.toUpperCase()]: ChartType.LINE,
      [ChartType.PIE.toUpperCase()]: ChartType.PIE,
      [ChartType.POLARAREA.toUpperCase()]: ChartType.POLARAREA,
      [ChartType.RADAR.toUpperCase()]: ChartType.RADAR,
      [ChartType.KPI.toUpperCase()]: ChartType.KPI,
      [ChartType.SCATTER.toUpperCase()]: ChartType.SCATTER,
    };

    const normalizeChartType = (type: string): string => chartTypeMap[type.toUpperCase()] ?? type.toLowerCase();

    const toArray = <T>(value: T | T[] | undefined): T[] => {
      if (Array.isArray(value)) {
        return value;
      }

      if (value === undefined || value === null) {
        return [];
      }

      return [value];
    };

    const collectChartVariants = (
      widget?: PublicMetricChartWidget,
    ): PublicMetricChartVariant[] => {
      if (!widget) {
        return [];
      }

      const collected: PublicMetricChartVariant[] = [];

      if (Array.isArray(widget.variants) && widget.variants.length) {
        collected.push(...widget.variants);
      }

      if (Array.isArray(widget.groups) && widget.groups.length) {
        widget.groups.forEach((group) => {
          if (Array.isArray(group.variants) && group.variants.length) {
            collected.push(...group.variants);
          }
        });
      }

      return collected;
    };

    const mergePlaceholders = (
      existing: string | string[] | undefined,
      incoming: string | string[] | undefined,
    ): string | string[] | undefined => {
      const current = toArray(existing);
      toArray(incoming).forEach((value) => {
        if (!current.includes(value)) {
          current.push(value);
        }
      });

      if (!current.length) {
        return undefined;
      }

      return current.length === 1 ? current[0] : current;
    };

    const mergeAvailableValues = (
      existing: unknown[] | undefined,
      incoming: unknown[] | undefined,
    ): unknown[] | undefined => {
      const result: unknown[] = Array.isArray(existing) ? [...existing] : [];
      if (Array.isArray(incoming)) {
        incoming.forEach((value) => {
          if (!result.some((candidate) => JSON.stringify(candidate) === JSON.stringify(value))) {
            result.push(value);
          }
        });
      }

      return result.length ? result : undefined;
    };

    const resolveMetadataEntry = (
      filterKey: string,
      metadataSources: (PublicMetricFiltersMetadata | undefined)[],
    ): PublicMetricFilterMetadata | undefined => metadataSources.reduce<PublicMetricFilterMetadata | undefined>(
      (acc, metadata) => {
        const entry = metadata?.[filterKey];
        if (!entry) {
          return acc;
        }

        const placeholder = mergePlaceholders(acc?.placeholder, entry.placeholder);
        const availableValues = mergeAvailableValues(acc?.availableValues, entry.availableValues);

        if (placeholder === undefined && availableValues === undefined) {
          return acc;
        }

        return {
          ...(placeholder !== undefined ? { placeholder } : {}),
          ...(availableValues !== undefined ? { availableValues } : {}),
        };
      },
      undefined,
    );

    const resolveFilterKeys = (
      availableFilters: string[] | undefined,
      metadataSources: (PublicMetricFiltersMetadata | undefined)[],
    ): string[] => {
      const keys = new Set<string>(availableFilters ?? []);
      metadataSources.forEach((metadata) => {
        if (metadata) {
          Object.keys(metadata).forEach((key) => keys.add(key));
        }
      });

      return Array.from(keys);
    };

    const resolveFilterTranslationCandidates = (
      filterKey: string,
      placeholder?: string | string[],
    ): string[] => {
      const candidates = new Set<string>();
      toArray(placeholder)
        .map((value) => value.replace(/^:/, ''))
        .forEach((value) => {
          if (!value) {
            return;
          }
          candidates.add(value);
          candidates.add(value.replace(/_integers?$/, ''));
        });

      candidates.add(filterKey);

      const normalizedKey = normalizeFilterKey(filterKey);
      if (normalizedKey !== filterKey) {
        candidates.add(normalizedKey);
      }

      return Array.from(candidates).filter(Boolean);
    };

    const isChannelFilter = (
      filterKey: string,
      placeholder?: string | string[],
    ): boolean => {
      const normalizedKey = normalizeFilterKey(filterKey).toLowerCase();
      if (normalizedKey.includes('channel')) {
        return true;
      }

      if (!placeholder) {
        return false;
      }

      return toArray(placeholder)
        .map((value) => value.replace(/^:/, '').toLowerCase())
        .some((value) => value.includes('channel'));
    };

    const mergeRawValues = (
      existing: string[] | undefined,
      incoming: string[],
    ): string[] => {
      const values = new Set<string>();

      existing?.forEach((entry) => {
        const trimmed = entry.trim();
        if (trimmed) {
          values.add(trimmed);
        }
      });

      incoming.forEach((entry) => {
        const trimmed = entry.trim();
        if (trimmed) {
          values.add(trimmed);
        }
      });

      return Array.from(values);
    };

    const resolveChannelTokens = (slug: string): string[] => getChannelTokens(slug);

    const resolveChannelDisplayNameFallback = (slug: string): string | undefined => getChannelDisplayName(slug);

    const extractFilterCode = (value: unknown): string => {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }

      if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        if (typeof record.code === 'string' || typeof record.code === 'number') {
          return String(record.code);
        }
        if (typeof record.value === 'string' || typeof record.value === 'number') {
          return String(record.value);
        }
        if (typeof record.id === 'string' || typeof record.id === 'number') {
          return String(record.id);
        }
      }

      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    };

    const formatFilterOption = (
      filterKey: string,
      placeholder: string | string[] | undefined,
      value: unknown,
    ): StatsPredefinedMetricFilterOption => {
      const code = extractFilterCode(value);
      const trimmedCode = code.trim();
      const translationKeys = resolveFilterTranslationCandidates(filterKey, placeholder);

      let name: string | null = null;
      translationKeys.some((translationKey) => {
        const key = `statsEditor.widgets.widgetData.config.field.filters.${translationKey}.${code}`;
        const translated = t(key);
        if (translated !== key) {
          name = translated;
          return true;
        }
        return false;
      });

      if (!name && value && typeof value === 'object' && 'label' in (value as Record<string, unknown>)) {
        const label = (value as Record<string, unknown>).label;
        if (typeof label === 'string') {
          name = label;
        }
      }

      if (!name && value && typeof value === 'object' && 'displayName' in (value as Record<string, unknown>)) {
        const displayName = (value as Record<string, unknown>).displayName;
        if (typeof displayName === 'string') {
          name = displayName;
        }
      }

      if (!name) {
        name = trimmedCode || code;
      }

      let legacyValue: string | undefined;
      let normalizedValue: string | undefined;
      let rawValues: string[] | undefined;

      if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;

        if (typeof record.legacyValue === 'string') {
          legacyValue = record.legacyValue;
        }

        if (typeof record.normalizedValue === 'string' || typeof record.normalizedValue === 'number') {
          normalizedValue = String(record.normalizedValue);
        }

        if (Array.isArray(record.rawValues)) {
          rawValues = record.rawValues
            .map((entry) => (entry === undefined || entry === null ? '' : String(entry).trim()))
            .filter((entry) => entry.length > 0);
        }
      }

      if (isChannelFilter(filterKey, placeholder)) {
        const channelTokens = resolveChannelTokens(trimmedCode || code);
        if (channelTokens.length) {
          rawValues = mergeRawValues(rawValues, channelTokens);
          if (!legacyValue) {
            const numericToken = channelTokens.find((token) => /^\d+$/.test(token));
            if (numericToken) {
              legacyValue = numericToken;
            }
          }
          if (!name || name === trimmedCode || name === code) {
            const fallbackDisplayName = resolveChannelDisplayNameFallback(trimmedCode || code);
            if (fallbackDisplayName) {
              name = fallbackDisplayName;
            }
          }
        }
      }

      const allValues = [
        trimmedCode || code,
        ...(legacyValue ? [legacyValue] : []),
        ...(normalizedValue ? [normalizedValue] : []),
        ...(rawValues ?? []),
      ]
        .map((entry) => entry.trim())
        .filter((entry, index, array) => entry.length > 0 && array.indexOf(entry) === index);

      return {
        name,
        code: trimmedCode || code,
        ...(legacyValue ? { legacyValue } : {}),
        ...(normalizedValue ? { normalizedValue } : {}),
        ...(allValues.length ? { rawValues: allValues } : {}),
      };
    };

    const normalizeSavedFilterValues = (values: string[]): string[] => values
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    const matchFilterOptionWithCode = (
      options: StatsPredefinedMetricFilterOption[] | undefined,
      code: string,
    ): StatsPredefinedMetricFilterOption | undefined => {
      if (!options || !options.length) {
        return undefined;
      }

      const normalizedCode = code.trim();
      if (!normalizedCode) {
        return undefined;
      }

      const normalizedCodeLower = normalizedCode.toLowerCase();

      const matched = options.find((option) => {
        if (!option) {
          return false;
        }

        const optionCode = option.code?.trim();
        if (optionCode && optionCode.toLowerCase() === normalizedCodeLower) {
          return true;
        }

        const rawValues = option.rawValues ?? [];
        if (rawValues.some((value) => value.trim().toLowerCase() === normalizedCodeLower)) {
          return true;
        }

        if (
          option.legacyValue &&
          option.legacyValue.trim().toLowerCase() === normalizedCodeLower
        ) {
          return true;
        }

        if (
          option.normalizedValue &&
          option.normalizedValue.trim().toLowerCase() === normalizedCodeLower
        ) {
          return true;
        }

        return false;
      });

      if (!matched) {
        return undefined;
      }

      return { ...matched };
    };

    const restoreFilterSelections = (
      options: StatsPredefinedMetricFilterOption[] | undefined,
      savedValues: string[],
    ): StatsPredefinedMetricFilterOption[] => {
      if (!options || !options.length || !savedValues.length) {
        return [];
      }

      const normalizedSavedValues = normalizeSavedFilterValues(savedValues);
      if (!normalizedSavedValues.length) {
        return [];
      }

      const selections: StatsPredefinedMetricFilterOption[] = [];

      normalizedSavedValues.forEach((savedValue) => {
        const matched = matchFilterOptionWithCode(options, savedValue);
        if (!matched) {
          return;
        }

        if (
          !selections.some(
            (selection) =>
              selection.code?.trim().toLowerCase() === matched.code?.trim().toLowerCase(),
          )
        ) {
          selections.push(matched);
        }
      });

      return selections;
    };

    const buildFilters = (
      filterKeys: string[],
      metadataSources: (PublicMetricFiltersMetadata | undefined)[],
    ): StatsPredefinedMetricFilters => filterKeys.reduce<StatsPredefinedMetricFilters>((acc, key) => {
      const normalizedKey = normalizeFilterKey(key);

      const metadata = resolveMetadataEntry(key, metadataSources);
      if (!metadata?.availableValues || !metadata.availableValues.length) {
        return acc;
      }

      const options = metadata.availableValues.reduce<StatsPredefinedMetricFilterOption[]>((list, value) => {
        const option = formatFilterOption(key, metadata.placeholder, value);
        if (!list.some((existing) => existing.code === option.code)) {
          list.push(option);
        }
        return list;
      }, []);

      if (options.length) {
        acc[normalizedKey] = options;
      }

      return acc;
    }, {} as StatsPredefinedMetricFilters);

    const cloneFilters = (filters: StatsPredefinedMetricFilters): StatsPredefinedMetricFilters => {
      const cloned: StatsPredefinedMetricFilters = {};
      (Object.keys(filters) as Array<keyof StatsPredefinedMetricFilters>).forEach((key) => {
        const options = filters[key];
        if (options) {
          cloned[key] = options.map((option) => ({ ...option }));
        }
      });

      return cloned;
    };

    const resolveLabel = (translationKey: string, fallback: string): string => {
      const translated = t(translationKey);
      if (translated !== translationKey) {
        return translated;
      }

      if (fallback) {
        const fallbackTranslation = t(fallback);
        if (fallbackTranslation !== fallback) {
          return fallbackTranslation;
        }
      }

      return fallback;
    };

    const buildMetricOption = (
      groupId: string,
      metric: PublicMetricDefinition,
    ): StatsPredefinedMetricOption | null => {
      const [metricGroup, metricCategory, metricKey] = metric.id.split('.');
      const resolvedGroup = metricGroup || groupId;
      const categoryKey = metricCategory || metric.category;
      const metricIdentifier = metricKey ?? metric.id;

      const value: StatsPredefinedMetricValue = {};
      let supportsTotals: boolean | undefined;

      const metricMetadataSources = [metric.filtersMetadata];

      if (metric.availableWidgets.kpi) {
        value[StatsWidgetType.KPI] = buildFilters(
          resolveFilterKeys(
            metric.availableWidgets.kpi.availableFilters,
            [metric.availableWidgets.kpi.filtersMetadata, ...metricMetadataSources],
          ),
          [metric.availableWidgets.kpi.filtersMetadata, ...metricMetadataSources],
        );

        // Forwarder la sentinelle Phase 6 vers le composant config dédié
        // (`StatsWidgetDataConfig.vue` lit `value[type].custom` pour switcher).
        if ((metric.availableWidgets.kpi as any).custom) {
          (value[StatsWidgetType.KPI] as any).custom = (metric.availableWidgets.kpi as any).custom;
        }

        if (metric.availableWidgets.kpi.supportsTotals === false) {
          supportsTotals = false;
        }
      }

      if (metric.availableWidgets.table) {
        const tableFilters = buildFilters(
          resolveFilterKeys(
            metric.availableWidgets.table.availableFilters,
            [metric.availableWidgets.table.filtersMetadata, ...metricMetadataSources],
          ),
          [metric.availableWidgets.table.filtersMetadata, ...metricMetadataSources],
        );

        // Ajouter les colonnes disponibles et par défaut
        value[StatsWidgetType.TABLE] = {
          ...tableFilters,
          ...(metric.availableWidgets.table.availableColumns ? {
            availableColumns: metric.availableWidgets.table.availableColumns,
          } : {}),
          ...(metric.availableWidgets.table.defaultColumns ? {
            defaultColumns: metric.availableWidgets.table.defaultColumns,
          } : {}),
          ...((metric.availableWidgets.table as any).custom
            ? { custom: (metric.availableWidgets.table as any).custom }
            : {}),
          // Sentinelle pour les Tableaux bruts auto-générés sur les data
          // custom (StatsWidgetDataConfig.vue lit `value[type].rawCustomData`
          // pour switcher vers `RawCustomDataTableWidgetDataConfig`).
          ...((metric.availableWidgets.table as any).rawCustomData
            ? { rawCustomData: (metric.availableWidgets.table as any).rawCustomData }
            : {}),
          // Sections meta pour les tableaux natifs étendus (contact /
          // custom data liée). Utilisé par StatsWidgetDataConfig.vue pour
          // grouper le MultiSelect "Colonnes à afficher".
          ...((metric.availableWidgets.table as any).sectionsMeta
            ? { sectionsMeta: (metric.availableWidgets.table as any).sectionsMeta }
            : {}),
        };
      }

      const chartWidget = metric.availableWidgets.chart;
      const chartVariants = collectChartVariants(chartWidget);
      const isCustomChartWidget = !!chartWidget && !!(chartWidget as any).custom;

      // Custom KPI : chart sans variants/groups doit quand même être exposé.
      // Le composant CustomKpiWidgetDataConfig prendra le relais.
      if (isCustomChartWidget && chartVariants.length === 0) {
        value[StatsWidgetType.CHART] = {
          custom: (chartWidget as any).custom,
        } as any;
      }

      if (chartVariants.length) {
        const chartValue: Record<string, Record<string, StatsPredefinedMetricFilters>> & {
          groups?: Record<string, { label?: string; labelKey?: string; variants: string[] }>;
          __variantsMeta?: Record<string, {
            dimension?: string;
            measure?: string;
            dimensionLabelKey?: string;
            measureLabelKey?: string;
          }>;
        } = {};

        const chartGroupMap = Array.isArray(chartWidget?.groups)
          ? chartWidget?.groups?.reduce<Record<string, { label?: string; labelKey?: string; variants: string[] }>>((acc, group) => {
            const variants = Array.isArray(group.variants)
              ? group.variants
                .map((variant) => variant.key)
                .filter((key): key is string => Boolean(key))
              : [];

            if (!variants.length) {
              return acc;
            }

            const label = resolveLabel(
              `statsEditor.widgets.widgetData.config.field.category.groups.${resolvedGroup}.categories.${group.key}.label`,
              group.name ?? group.key,
            );

            acc[group.key] = {
              label,
              // Système v3 : forwarder labelKey si présent côté API.
              ...((group as any).labelKey ? { labelKey: (group as any).labelKey } : {}),
              variants,
            };

            return acc;
          }, {})
          : undefined;

        // Stockage parallèle des métadonnées de répartition v3 (games).
        const variantsMeta: Record<string, {
          dimension?: string;
          measure?: string;
          dimensionLabelKey?: string;
          measureLabelKey?: string;
        }> = {};

        chartVariants.forEach((variant) => {
          // Utiliser seulement les métadonnées spécifiques au variant, pas celles du niveau supérieur
          const variantMetadataSources = [variant.filtersMetadata];
          const variantFilterKeys = resolveFilterKeys(variant.availableFilters, variantMetadataSources);
          const variantFilters = buildFilters(variantFilterKeys, variantMetadataSources);

          const chartTypes = (variant.availableChartTypes.length
              ? variant.availableChartTypes
              : chartWidget?.availableChartTypes ?? []
          ).map((chartType) => normalizeChartType(chartType));

          if (!chartTypes.length) {
            return;
          }

          const filtersByChartType: Record<string, StatsPredefinedMetricFilters> = {};
          chartTypes.forEach((chartType) => {
            filtersByChartType[chartType] = cloneFilters(variantFilters);
          });

          chartValue[variant.key] = filtersByChartType;

          // Système v3 : capter les métadonnées de répartition si présentes.
          // Stockées dans __variantsMeta pour ne pas polluer chartValue[variant.key]
          // qui doit rester un Record<chartType, filters> pur (legacy iterations).
          const v = variant as any;
          if (v.dimension && v.measure) {
            variantsMeta[variant.key] = {
              dimension: v.dimension,
              measure: v.measure,
              dimensionLabelKey: v.dimensionLabelKey,
              measureLabelKey: v.measureLabelKey,
            };
          }
        });

        if (Object.keys(chartValue).length) {
          if (chartGroupMap && Object.keys(chartGroupMap).length) {
            chartValue.groups = chartGroupMap;
          }

          if (Object.keys(variantsMeta).length) {
            chartValue.__variantsMeta = variantsMeta;
          }

          value[StatsWidgetType.CHART] = chartValue;
        }
      }

      if (!Object.keys(value).length) {
        return null;
      }

      const childrenLabel = resolveLabel(
        `statsEditor.widgets.widgetData.config.field.category.groups.${resolvedGroup}.categories.${categoryKey}.metrics.${metricIdentifier}`,
        metric.name,
      );

      return {
        childrenLabel,
        group: resolvedGroup,
        category: categoryKey,
        metric: metricIdentifier,
        // Description : héritée du backend (pour les KPI custom : description
        // saisie à la création). Le composant picker la résout au rendu via
        // `getMetricDescription` qui préfère cette valeur, puis tente une clé
        // i18n `statsEditor.metricDescriptions.<group>.<category>.<metric>`
        // pour les natives, et fallback à null.
        description: metric.description ?? null,
        allowMultiple: chartWidget ? chartWidget.allowMultiple !== false : true,
        value,
        legacy: Boolean(metric.isLegacy),
        ...(supportsTotals === false ? { supportsTotals: false } : {}),
      };
    };

    const buildMetricGroups = (groups: PublicMetricGroup[]): StatsPredefinedMetricGroup[] => groups.reduce<StatsPredefinedMetricGroup[]>(
      (acc, group) => {
        const categoriesMap = new Map<string, StatsPredefinedMetricGroup['items'][number]>();

        group.metrics.forEach((metric) => {
          const metricOption = buildMetricOption(group.id, metric);
          if (!metricOption) {
            return;
          }

          if (!categoriesMap.has(metricOption.category)) {
            const categoryLabel = resolveLabel(
              `statsEditor.widgets.widgetData.config.field.category.groups.${metricOption.group}.categories.${metricOption.category}.label`,
              metric.category,
            );

            categoriesMap.set(metricOption.category, {
              key: metricOption.category,
              groupKey: metricOption.group,
              label: categoryLabel,
              value: [],
            });
          }

          categoriesMap.get(metricOption.category)?.value.push(metricOption);
        });

        const items = Array.from(categoriesMap.values()).filter((category) => category.value.length > 0);
        if (!items.length) {
          return acc;
        }

        const groupLabel = resolveLabel(
          `statsEditor.widgets.widgetData.config.field.category.groups.${group.id}.label`,
          group.name ?? group.id,
        );

        acc.push({
          key: group.id,
          label: groupLabel,
          items,
        });

        return acc;
      },
      [],
    );

    const pageSettingState = computed(() => {
      const pageSetting = store.getters['statsEditor/getPageSettings'];

      try {
        const name = JSON.parse(pageSetting.name);
        const navigationName = JSON.parse(pageSetting.navigationName);

        pageSetting.name = name;
        pageSetting.navigationName = navigationName;

        return pageSetting;
      } catch (error) {
        return pageSetting;
      }
    });

    const pageSettingStateName = computed(() => {
      if (typeof pageSettingState.value.name !== 'string') {
        if (locale.value in pageSettingState.value.name) {
          return pageSettingState.value.name[locale.value];
        }
        return pageSettingState.value.name.fr;
      }
      return pageSettingState.value.name;
    });

    const pageTypeState = computed(() => store.getters['statsEditor/getPageType']);
    const pageRowsState = computed({
      get() {
        return store.getters['statsEditor/getPageRows'];
      },
      set(newValue) {
        store.commit('statsEditor/setPageRows', newValue);
      },
    });
    const formValidation = ref<any>({});

    /* Filter configuration */
    const canConfigureFilters = computed(() => store.getters['statsEditor/getCanConfigureFilters']);

    /* Widget panel */
    const leftToolbarState = computed(() => store.getters['statsEditor/getLeftToolbar']);
    const selectedWidgetState: Ref<StatsBuilderSelectedWidget|null> = computed(() => store.getters['statsEditor/getSelectedWidget']);

    /* Widget Data Metrics */
    const groupedCategoriesPredefinedMetrics = ref<StatsPredefinedMetricGroup[]>([]);

    /* Temp Rows */
    const tempRow = computed(() => store.getters['statsEditor/getTempRow']);

    /* Remove temp row */
    const removeTempRow = async () => {
      if (tempRow.value) {
        await nextTick();
        store.dispatch('statsEditor/removeRow', {
          location: {
            rowId: tempRow.value.rowId,
            columnId: tempRow.value.columnId,
            panelData: tempRow.value.panelData,
          },
        });
      }
    };

    const closeWidgetEditorPanel = async () => {
      formValidation.value = {};
      store.commit('statsEditor/hideLeftToolbar');
      store.commit('statsEditor/resetSelectedWidget');
      store.dispatch('statsEditor/removePlaceholderRows');

      /** Remove temp row */
      await removeTempRow();
    };

    const leavePage = () => {
      store.commit('statsEditor/resetState');
      store.commit('statsEditor/setRefreshComponents', true);
      store.commit('statsEditor/setRefreshNavigationBar', true);
      store.commit('statsEditor/resetTempRow');
    };

    function removeColumn(row: any, idColumn: string) {
      if (row.type === 'StatsWidgetRow') {
        for (let j = 0; j < row.children.length; j++) {
          const column = row.children[j];
          if (column.type === 'StatsWidgetColumn' && column.id === idColumn) {
            const { layout } = column;
            row.children.splice(j, 1);
            if (row.children.length > 0) {
              // eslint-disable-next-line radix,no-param-reassign
              row.children[0].layout = (parseInt(row.children[0].layout) + parseInt(layout)).toString();

              const newRowLayout = [];
              for (let k = 0; k < row.children.length; k++) {
                newRowLayout.push(row.children[k].layout);
              }
              // eslint-disable-next-line no-param-reassign
              row.layout = newRowLayout.join('-');
            }
            return;
          }
        }
      }
    }

    const checkWidgetTabs = (tabs: any[], remove: boolean): boolean => {
      let check = true;

      for (let i = 0; i < tabs.length; i++) {
        if ('children' in tabs[i]) {
          if (!tabs[i].children.length) {
            if (remove) {
              tabs.splice(i, 1);
            }
            check = false;
          } else {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            check = checkRows(tabs[i].children, remove);
          }
        } else if ('items' in tabs[i]) {
          if (!tabs[i].items.length) {
            if (remove) {
              tabs.splice(i, 1);
              break;
            }
            check = false;
          } else {
            check = checkWidgetTabs(tabs[i].items, remove);
          }
        }
        if (!check && !remove) {
          break;
        }
      }
      return check;
    };

    const checkRows = (rows: StatsBuilderRowConfig[], remove = false): boolean => {
      let check = true;
      // disable check rows protection
      return check;
      if (rows.length) {
        for (let i = 0; i < rows.length; i++) {
          for (let j = 0; j < rows[i].children.length; j++) {
            const { widgets } = rows[i].children[j];
            if (widgets === null) {
              check = false;
              if (remove) {
                removeColumn(rows[i], rows[i].children[j].id);
                if (!rows[i].children.length) {
                  rows.splice(i, 1);
                  break;
                }
              }
            } else if (widgets.type === 'StatsWidgetTabs') {
              if (!widgets.tabs.length) {
                check = false;
                if (remove) {
                  removeColumn(rows[i], rows[i].children[j].id);
                  if (!rows[i].children.length) {
                    rows.splice(i, 1);
                    break;
                  }
                }
              } else {
                check = checkWidgetTabs(widgets.tabs, remove);
              }
            } else if (widgets.type === 'StatsWidgetMenu') {
              if (!widgets.toolbar.length) {
                check = false;
                if (remove) {
                  removeColumn(rows[i], rows[i].children[j].id);
                  if (!rows[i].children.length) {
                    rows.splice(i, 1);
                    break;
                  }
                }
              } else {
                check = checkWidgetTabs(widgets.toolbar, remove);
              }
            }
          }

          if (!check && !remove) {
            break;
          }
        }
      }

      return check;
    };

    const savePage = (leave: boolean) => {
      store.commit('general/showTheSpinner');

      let extra_data: any = null;
      if (pageTypeState.value === StatsType.EMAIL) {
        extra_data = {
          mail_subject: (pageSettingState.value as GlobalEmailSettingsData).mail_subject,
          mailing_frequency: (pageSettingState.value as GlobalEmailSettingsData).mailing_frequencies,
        };
      }

      if (extra_data) {
        extra_data.defaultFilters = store.getters['statsEditor/getPageFilters'];
      } else {
        extra_data = {
          defaultFilters: store.getters['statsEditor/getPageFilters'],
        };
      }

      if (extra_data) {
        extra_data = JSON.stringify(extra_data);
      }

      const usersPagesConfig: UsersPagesConfigurationUpdateInputItem[] = [{
        id_users_pages_configuration: store.getters['statsEditor/getPageId'],
        page_name: JSON.stringify(pageSettingState.value.name),
        navigation_name: Object.prototype.hasOwnProperty.call(pageSettingState.value, 'navigationName') ? JSON.stringify(pageSettingState.value.navigationName) : JSON.stringify(pageSettingState.value.name),
        configuration: JSON.stringify(pageRowsState.value),
        extra_data,
        date_modification: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
      }];

      UpdateUsersPagesConfiguration(usersPagesConfig).then((result) => {
        if (result.status && result.id) {
          showToastSuccess(t('statsEditor.successSave'));
          if (leave) {
            leavePage();
          }
        } else {
          showToastError(t('statsEditor.errorSave'));
        }
        store.commit('general/hideTheSpinner');
      }).catch((error) => {
        showToastError(t('statsEditor.errorSave'));
        store.commit('general/hideTheSpinner');
      });
    };

    const savePageConfirmationDialog = async (leave: boolean) => {
      /** Remove temp row */
      await removeTempRow();

      const validation = await pageSettingsValidate(pageSettingState.value, pageTypeState.value, localizedTextInputValidator);
      formValidation.value = validation.validate;
      if (!validation.success) {
        if (!leftToolbarState.value.show || leftToolbarState.value.panel !== StatsEditorPanelEnum.SETTINGS_PANEL) {
          await closeWidgetEditorPanel();
          store.commit('statsEditor/showLeftToolbar', StatsEditorPanelEnum.SETTINGS_PANEL);
        }
        await showToastError(t('errorMessages.FORM_ERROR'));
      } else if (!checkRows(pageRowsState.value as StatsBuilderRowConfig[], false)) {
        confirm.require({
          group: 'statsEditor',
          message: t('statsEditor.checkBeforeSave.message'),
          header: t('statsEditor.checkBeforeSave.header'),
          acceptLabel: t('statsEditor.checkBeforeSave.acceptLabel'),
          rejectLabel: t('statsEditor.checkBeforeSave.rejectLabel'),
          accept: () => {
            const cloneRows = JSON.parse(JSON.stringify(pageRowsState.value));
            do {
              checkRows(cloneRows, true);
            } while (!checkRows(cloneRows, false));

            store.commit('statsEditor/setPageRows', cloneRows);
            savePage(leave);
          },
        });
      } else {
        savePage(leave);
      }
    };

    /* Header and second toolbar buttons */
    const toolbarHeaderRightButtons: PrimvueMenuModel[] = [
      {
        label: t('statsEditor.headerToolbar.save'),
        class: 'p-button-secondary',
        items: [
          {
            label: t('statsEditor.headerToolbar.saveAndLeave'),
            command: () => savePageConfirmationDialog(true),
          },
          {
            label: t('statsEditor.headerToolbar.save'),
            command: () => savePageConfirmationDialog(false),
          },
          {
            label: t('statsEditor.headerToolbar.leaveWithoutSaving'),
            command: () => leavePage(),
          },
        ],
      },
    ];
    const secondToolbarLeftButtons = computed(() => ([
      {
        icon: 'far fa-fw fa-cog',
        label: t('statsEditor.toolbar.label.settings'),
        tooltip: t('statsEditor.toolbar.tooltip.settings'),
        class: () => (leftToolbarState.value.show
        && leftToolbarState.value.panel === StatsEditorPanelEnum.SETTINGS_PANEL ? 'active' : ''),
        command: () => {
          if (leftToolbarState.value.show
            && leftToolbarState.value.panel === StatsEditorPanelEnum.SETTINGS_PANEL) {
            closeWidgetEditorPanel();
          } else {
            closeWidgetEditorPanel();
            store.commit('statsEditor/showLeftToolbar', StatsEditorPanelEnum.SETTINGS_PANEL);
          }
        },
      },
      {
        icon: 'far fa-fw fa-th-large',
        label: t('Elements'),
        tooltip: t('Elements'),
        class: () => (leftToolbarState.value.show
        && leftToolbarState.value.panel === StatsEditorPanelEnum.ELEMENTS_LIST_PANEL ? 'active' : ''),
        command: () => {
          if (leftToolbarState.value.show
            && leftToolbarState.value.panel === StatsEditorPanelEnum.ELEMENTS_LIST_PANEL) {
            closeWidgetEditorPanel();
          } else {
            closeWidgetEditorPanel();
            store.commit('statsEditor/showLeftToolbar', StatsEditorPanelEnum.ELEMENTS_LIST_PANEL);
          }
        },
      },
      {
        icon: 'far fa-filter',
        label: t('Filtres'),
        tooltip: t('Filtres'),
        class: () => (leftToolbarState.value.show
        && leftToolbarState.value.panel === StatsEditorPanelEnum.FILTERS_PANEL ? 'active' : ''),
        command: () => {
          // Ne rien faire si le panel Filtres est déjà ouvert
          if (leftToolbarState.value.show
            && leftToolbarState.value.panel === StatsEditorPanelEnum.FILTERS_PANEL) {
            store.commit('statsEditor/hideLeftToolbar');
          } else if (!leftToolbarState.value.show
            || leftToolbarState.value.panel !== StatsEditorPanelEnum.FILTERS_PANEL) {
            store.commit('statsEditor/showLeftToolbar', StatsEditorPanelEnum.FILTERS_PANEL);
          }
        },
        hide: !canConfigureFilters.value,
      },
      {
        icon: 'far fa-fw fa-paper-plane',
        label: null,
        tooltip: t('statsEditor.toolbar.label.sendTest'),
        command: () => {
          // Open modal to select mail addresses to send the reports
          store.commit('general/showSendTestModal');
        },
        hide: pageTypeState.value !== StatsType.EMAIL,
      }]));

    const saveWidget = async () => {
      // validate form then save widget
      store.dispatch('statsEditor/saveWidget').then(async (response) => {
        if (response.success) {
          await showToastSuccess(t('statsEditor.toastMessages.saveWidget.success'));
          store.commit('statsEditor/resetTempRow');
          await closeWidgetEditorPanel();
          await nextTick();
          await store.dispatch('statsEditor/removePlaceholderRows');
        } else {
          showToastError(t('errorMessages.FORM_ERROR')).then(() => {
            formValidation.value = response.validate;
          });
        }
      });
    };

    onMounted(async () => {
      const metricsResponse: any = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/stats/predefined-metrics',
        {},
        '',
        undefined,
        true,
        { view: 'grouped' },
      );

      if (!metricsResponse || metricsResponse.error || metricsResponse.success === false) {
        await showToastError('GENERIC_ERROR');
        return;
      }

      const metricsData: PublicMetricGroup[] = metricsResponse.data ?? metricsResponse;
      if (!Array.isArray(metricsData)) {
        await showToastError('GENERIC_ERROR');
        return;
      }

      groupedCategoriesPredefinedMetrics.value = buildMetricGroups(metricsData);
    });

    /**
     * Send test report by e-mail
     * @param params
     */
    const sendTestReport = async (params: Suggestion[]) => {
      let extraData: any = null;
      if (pageTypeState.value === StatsType.EMAIL) {
        extraData = {
          mail_subject: (pageSettingState.value as GlobalEmailSettingsData).mail_subject,
          mailing_frequency: (pageSettingState.value as GlobalEmailSettingsData).mailing_frequencies,
        };
      }

      if (extraData) {
        extraData.defaultFilters = store.getters['statsEditor/getPageFilters'];
      } else {
        extraData = {
          defaultFilters: store.getters['statsEditor/getPageFilters'],
        };
      }

      if (extraData) {
        extraData = JSON.stringify(extraData);
      }

      nestCall('post', NestApiType.ESR, '/esr/send-test', {}, '', {
        idShop: UserState.activeShop?.id ?? 0,
        idReport: parseInt(store.getters['statsEditor/getPageId'], 10),
        addresses: params,
        configuration: JSON.stringify(pageRowsState.value),
        extraData,
      }).then(async (result) => {
        if (result && result.success) {
          await showToastSuccess('emailReportSent');
        } else {
          await showToastError('GENERIC_ERROR');
        }
      });
    };

    return {
      t,
      pageTypeState,
      pageRowsState,
      leftToolbarState,
      StatsType,
      StatsEditorPanelEnum,
      toolbarHeaderRightButtons,
      secondToolbarLeftButtons,
      selectedWidgetState,
      saveWidget,
      closeWidgetEditorPanel,
      formValidation,
      groupedCategoriesPredefinedMetrics,
      pageSettingStateName,
      sendTestReport,
    };
  },
});
</script>

<style lang="scss">
.surface-ground {
  height: 100%;
}
.content {
  overflow-y: auto;
}
.panel-sidebar-builder-stats {
  .p-panel-header {
    border: none !important;
    border-bottom: 1px solid #dee2e6 !important;
    border-radius: 0 !important;

    .panel-header-content {
      display: flex;
      align-items: center;
    }
  }

  .icons-header-panel {
    cursor: pointer;
    color: #607D8B;
  }
}

.configuration-wrapper {
  .configuration-action-container {
    margin-top: 2rem !important;
  }
}
</style>
